import React, { memo, useContext, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  SearchBar,
  PagesectionSubsection,
} from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';

import { CountingCirclesRecord } from 'api/api.types';
import replaceUmlaute from 'utils/replaceUmlaute';
import { VOTERS_ABROAD_NAME } from 'lib/Map/WorldMap';
import ApiContext from 'api/ApiContext';
import MapSwitch, { MuniTab } from 'lib/MapSwitch/MapSwitch';

// enforce that exactly one of the two props is passed
type Records =
  | {
      municipalities: CountingCirclesRecord[];
      districts?: never;
      prevDistricts?: never;
    }
  | {
      districts: CountingCirclesRecord[];
      prevDistricts?: CountingCirclesRecord[];
      municipalities?: never;
    };

interface BaseProps {
  render: (
    municipality: CountingCirclesRecord,
    prevDistrict?: CountingCirclesRecord,
  ) => JSX.Element;
  // only used in memo comparison
  // eslint-disable-next-line react/no-unused-prop-types
  checksum: string;
  hideSearchbar?: boolean;
  showNumberOfSeats?: boolean;
  selectedMuni?: CountingCirclesRecord;
  setSelectedMuni?: (municipality: CountingCirclesRecord) => void;
  addMapSwitch?: boolean;
  selectedMuniTab?: MuniTab;
  setSelectedMuniTab?: (tab: MuniTab) => void;
}

type Props = BaseProps & Records;

// ########## CAREFUL ##########
// This component is wrapped in a memo() call, so it will not re-render when its props change.
const MunicipalityList = ({
  municipalities,
  districts,
  prevDistricts,
  render,
  hideSearchbar,
  showNumberOfSeats,
  selectedMuni,
  setSelectedMuni,
  addMapSwitch,
  selectedMuniTab,
  setSelectedMuniTab,
}: Props) => {
  const records = districts ?? municipalities;
  const { t } = useTranslation('wap');
  const { language } = useContext(ApiContext);

  const [search, setSearch] = useState(selectedMuni?.name[language] ?? '');

  const updateSearch = (value: string) => {
    setSearch(value);
    if (value === '' && setSelectedMuni) {
      setSelectedMuni(null);
    }
  };

  const filteredMunicipalities = records?.filter((record) =>
    record.name.de.toLowerCase().includes(search.toLowerCase()),
  );

  // KTAG standards require Umlaute to be treated as ae, oe, ue in sorting
  const sortMunis = (a: CountingCirclesRecord, b: CountingCirclesRecord) => {
    const aName = replaceUmlaute(a?.name?.de);
    const bName = replaceUmlaute(b?.name?.de);
    return aName?.localeCompare(bName);
  };

  filteredMunicipalities?.sort(sortMunis);

  const votersAbroadIndex = filteredMunicipalities?.findIndex(
    (municipality) => municipality.name.de === VOTERS_ABROAD_NAME,
  );
  if (votersAbroadIndex !== -1) {
    const foreignVotersEntry = filteredMunicipalities?.splice(votersAbroadIndex, 1);
    filteredMunicipalities?.push(...foreignVotersEntry);
  }

  const getNumberOfSeats = (municipality: CountingCirclesRecord) => {
    if (municipality?.seat_count) return municipality.seat_count;

    const seats = municipality?.candidates?.reduce(
      (acc, candidate) => acc + (candidate?.results.elected ? 1 : 0),
      0,
    );
    return seats;
  };

  const getMunicipalityTitle = (municipality: CountingCirclesRecord) => {
    if (showNumberOfSeats) {
      return `${municipality.name.de}: ${t('seats', {
        count: getNumberOfSeats(municipality),
      })}`;
    }

    if (!municipality?.finished) {
      return municipality.name.de + t('no_results');
    }

    return municipality.name.de;
  };

  const getRenderFunction = (municipality: CountingCirclesRecord) => {
    if (!municipality?.finished) {
      return null;
    }

    if (prevDistricts) {
      const prevDistrict = prevDistricts.find(
        (d) => d.bfs_number === municipality.bfs_number,
      );
      return render(municipality, prevDistrict);
    }

    return render(municipality);
  };

  return (
    <PagesectionSubsection type="slender" additionalClassNames="municipalitylist">
      {!hideSearchbar && (
        <PagesectionSubsection pull={1}>
          <div className="map__search">
            <SearchBar
              search={search}
              setSearch={updateSearch}
              placeholder={t('municipality_search')}
            />
            {addMapSwitch && (
              <MapSwitch
                selectedTab={selectedMuniTab}
                setSelectedTab={setSelectedMuniTab}
              />
            )}
          </div>
        </PagesectionSubsection>
      )}
      <div className="municipalitylist__accordion">
        <Accordion multiselectable unmountOnClose>
          {filteredMunicipalities?.map((municipality) => (
            <AccordionItem
              noUrlRewrite
              key={municipality.name.de}
              title={getMunicipalityTitle(municipality)}
              disabled={!municipality?.finished}
              expanded={
                selectedMuni?.bfs_number === municipality.bfs_number &&
                municipality.finished
              }
            >
              {getRenderFunction(municipality)}
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </PagesectionSubsection>
  );
};

// Prevent collapse of Accordions when data is refetched
// TODO: re-write the Accordion and AccordionItem components to keep their expanded state on render.
const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  if (!prevProps.municipalities && nextProps.municipalities) return false;
  if (!prevProps.districts && nextProps.districts) return false;
  if (!prevProps.prevDistricts && nextProps.prevDistricts) return false;
  return prevProps.checksum === nextProps.checksum;
};

export default memo(MunicipalityList, arePropsEqual);
